import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { useAppTranslation } from 'shared/hooks/useAppTranslation'
import { useClickOutside } from 'shared/hooks/useClickOutside'
import { languages, LanguagesType, languagesWithIcons } from 'shared/locale'

import { actions } from 'features/userSettings'
import { selectLanguage } from 'features/userSettings/selectors'

import { Portal } from 'components/UIKit/Portal'

import { ReactComponent as Arrow } from './img/arrow.svg'

import './SetLanguage.scss'

type SelectLangOption = {
  value: string
  suffix?: React.ReactNode
  prefix?: React.ReactNode
  label?: string
}

type DropdownPosition = 'top' | 'bottom'
type DropdownAlign = 'left' | 'right'

export type SetLanguageProps = {
  className?: string
  disabled?: boolean
  dropdownPosition?: DropdownPosition
  dropdownAlign?: DropdownAlign
}

const b = block('select-lang')
const DROPDOWN_WIDTH = 73

export const SetLanguage: React.FC<SetLanguageProps> = ({
  className,
  disabled,
  dropdownPosition,
  dropdownAlign = 'right',
}) => {
  const { t } = useAppTranslation()
  const dispatch = useDispatch()
  const lang = useSelector(selectLanguage, shallowEqual)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const languagesWithIconsArray = Object.values(languagesWithIcons)
  const options: SelectLangOption[] = languagesWithIconsArray
    .filter(language => [languages.EN, languages.FR, languages.AR].includes(language.lang as LanguagesType))
    .map(item => ({
      prefix: <img key={item.lang} src={item.icon} className={b('flag')} alt={item.text} />,
      value: item.lang,
      label: item.shortCode,
    }))

  const value = useMemo(() => {
    const selected = Object.values(languagesWithIcons).find(it => it.lang === lang)

    if (selected) {
      return {
        value: selected.lang,
        label: selected.shortCode,
      }
    }

    return undefined
  }, [lang])

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const left =
      dropdownAlign === 'left' ? rect.left : rect.right - DROPDOWN_WIDTH
    if (dropdownPosition === 'top') {
      setDropdownStyle({
        position: 'fixed',
        left,
        top: rect.top - 4,
        transform: 'translateY(-100%)',
      })
    } else {
      setDropdownStyle({
        position: 'fixed',
        left,
        top: rect.bottom + 4,
      })
    }
  }, [dropdownAlign, dropdownPosition])

  useLayoutEffect(() => {
    if (isOpened) updatePosition()
  }, [isOpened, updatePosition])

  const handleSelect = (option: SelectLangOption) => {
    dispatch(actions.changeLang(option.value as LanguagesType))
    setIsOpened(false)
  }

  useClickOutside(dropdownRef, () => {
    setIsOpened(false)
  }, triggerRef)

  const handleTriggerClick = () => {
    if (!disabled) setIsOpened(prev => !prev)
  }

  return (
    <div className={b({ isOpened }).mix(className)}>
      <button
        ref={triggerRef}
        type="button"
        className={b('trigger', { isOpened })}
        onClick={handleTriggerClick}
        disabled={disabled}>
        {value ? (
          <span className={b('selected-value')}>{value.label || value.value}</span>
        ) : (
          <span className={b('placeholder')}>{t('common.noOptions')}</span>
        )}
        <Arrow className={b('arrow', { isOpened })} />
      </button>

      {isOpened && (
        <Portal>
          <div
            ref={dropdownRef}
            className={b('dropdown', { position: dropdownPosition, align: dropdownAlign })}
            style={dropdownStyle}>
            <div className={b('options')}>
              {options.length === 0 ? (
                <div className={b('empty')}>{t('common.noOptions')}</div>
              ) : (
                options.map(option => (
                  <div
                    key={option.value}
                    className={b('option', { selected: value?.value === option.value })}
                    onClick={() => handleSelect(option)}>
                    {option.prefix}
                    {option.label || option.value}
                  </div>
                ))
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
